import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  RiBriefcase4Line,
  RiGroupLine,
  RiBankLine,
  RiNewspaperLine,
  RiTrophyLine,
  RiRefreshLine,
  RiCalendarLine,
  RiCloseLine,
  RiErrorWarningLine,
  RiFileList3Line,
  RiArrowDownSLine,
} from "react-icons/ri";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

const BASE_URL = `${process.env.REACT_APP_API_URL}/business-report`;
const BANK_BASE_URL = `${process.env.REACT_APP_API_URL}/bank`;

const Spinner = ({ className = "w-4 h-4" }) => (
  <AiOutlineLoading3Quarters className={`animate-spin ${className}`} />
);

const Toast = ({ message, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-xl shadow-xl text-sm font-medium bg-red-50 border border-red-200 text-red-800">
      <RiErrorWarningLine className="w-4 h-4 text-red-500" />
      <span>{message}</span>
      <button
        onClick={onClose}
        className="ml-2 opacity-50 hover:opacity-100 transition"
      >
        <RiCloseLine className="w-4 h-4" />
      </button>
    </div>
  );
};

const formatCurrency = (val) =>
  `₹${Number(val || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const formatNumber = (val) => Number(val || 0).toLocaleString("en-IN");

const getClientName = (client) => {
  if (!client) return "—";
  if (typeof client === "string") return "—";
  return (
    client.bankName ||
    client.name ||
    client.companyName ||
    client.organisation ||
    "—"
  );
};

const StatCard = ({ label, value, tone = "blue" }) => {
  const tones = {
    blue: "bg-blue-50 border-blue-100 text-blue-500 [&_.val]:text-slate-800",
    emerald:
      "bg-emerald-50 border-emerald-100 text-emerald-500 [&_.val]:text-emerald-700",
    red: "bg-red-50 border-red-100 text-red-400 [&_.val]:text-red-600",
    amber:
      "bg-amber-50 border-amber-100 text-amber-500 [&_.val]:text-amber-700",
  };
  return (
    <div className={`px-4 py-3 rounded-lg border ${tones[tone]}`}>
      <p className="text-[11px] uppercase font-semibold tracking-wide">
        {label}
      </p>
      <p className="val text-base font-bold mt-0.5">{value}</p>
    </div>
  );
};

const EmptyState = ({ message }) => (
  <div className="py-16 text-center">
    <div className="w-14 h-14 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center mx-auto mb-3">
      <RiFileList3Line className="w-7 h-7 text-blue-300" />
    </div>
    <span className="text-sm text-slate-400">{message}</span>
  </div>
);

const LoadingRows = ({ label }) => (
  <div className="py-16 flex flex-col items-center gap-3 text-blue-400">
    <Spinner className="w-7 h-7 text-blue-500" />
    <span className="text-sm">{label}</span>
  </div>
);

const Th = ({ children, align = "left" }) => (
  <th className={`px-4 py-3 text-${align}`}>
    <span className="text-xs font-semibold text-blue-500 uppercase tracking-widest whitespace-nowrap">
      {children}
    </span>
  </th>
);

const Td = ({ children, align = "left", className = "" }) => (
  <td className={`px-4 py-3 text-${align} whitespace-nowrap ${className}`}>
    {children}
  </td>
);

const Table = ({ headers, children }) => (
  <div className="overflow-x-auto border border-blue-100 rounded-xl">
    <table className="w-full text-sm">
      <thead>
        <tr className="bg-blue-50/50 border-b border-blue-100">
          {headers.map((h) => (
            <Th key={h.label || h} align={h.align}>
              {h.label || h}
            </Th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

// Mini table shown when a client-wise / bank-wise row is expanded
const ClientPeriodBreakdown = ({ periods }) => {
  if (!periods || periods.length === 0) {
    return (
      <span className="text-xs text-slate-400">
        No period breakdown available.
      </span>
    );
  }
  const sorted = [...periods].sort((a, b) => (a.period > b.period ? 1 : -1));
  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-blue-400 uppercase tracking-widest">
          <th className="text-left py-1 pr-4">Period</th>
          <th className="text-left py-1 pr-4">Revenue</th>
          <th className="text-left py-1 pr-4">Sub Total</th>
          <th className="text-left py-1 pr-4">CGST</th>
          <th className="text-left py-1 pr-4">SGST</th>
          <th className="text-left py-1">Invoices</th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((p) => (
          <tr key={p.period} className="border-t border-blue-100/60">
            <td className="py-1 pr-4 font-medium text-slate-700">{p.period}</td>
            <td className="py-1 pr-4 text-emerald-600">
              {formatCurrency(p.revenue)}
            </td>
            <td className="py-1 pr-4">{formatCurrency(p.subTotal)}</td>
            <td className="py-1 pr-4">{formatCurrency(p.cgst)}</td>
            <td className="py-1 pr-4">{formatCurrency(p.sgst)}</td>
            <td className="py-1">{p.invoiceCount}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// Mini table shown when a newspaper-wise row is expanded
const NewspaperPeriodBreakdown = ({ periods }) => {
  if (!periods || periods.length === 0) {
    return (
      <span className="text-xs text-slate-400">
        No period breakdown available.
      </span>
    );
  }
  const sorted = [...periods].sort((a, b) => (a.period > b.period ? 1 : -1));
  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-blue-400 uppercase tracking-widest">
          <th className="text-left py-1 pr-4">Period</th>
          <th className="text-left py-1 pr-4">Revenue</th>
          <th className="text-left py-1 pr-4">Ads</th>
          <th className="text-left py-1">Volume</th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((p) => (
          <tr key={p.period} className="border-t border-blue-100/60">
            <td className="py-1 pr-4 font-medium text-slate-700">{p.period}</td>
            <td className="py-1 pr-4 text-emerald-600">
              {formatCurrency(p.revenue)}
            </td>
            <td className="py-1 pr-4">{p.ads}</td>
            <td className="py-1">{formatNumber(p.volume)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const TABS = [
  { key: "client-wise", label: "Client-wise Report", icon: RiGroupLine },
  { key: "bank-wise", label: "Bank-wise Report", icon: RiBankLine },
  {
    key: "newspaper-wise",
    label: "Newspaper-wise Report",
    icon: RiNewspaperLine,
  },
  { key: "top-clients", label: "Top 10 Clients", icon: RiTrophyLine },
];

export default function BusinessReports() {
  const [activeTab, setActiveTab] = useState("client-wise");
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [groupBy, setGroupBy] = useState("month"); // month | quarter | year
  const [limit, setLimit] = useState(10);
  const [clientId, setClientId] = useState("");
  const [clients, setClients] = useState([]);

  const notify = (message) => setToast({ message });

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${BANK_BASE_URL}/get-banks`);
      setClients(res.data?.data || res.data || []);
    } catch {
      // Non-fatal: bank-wise tab falls back to manual client id entry
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setReportData(null);
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      if (activeTab === "top-clients") {
        params.limit = limit;
      } else {
        params.groupBy = groupBy;
      }

      if (activeTab === "bank-wise") {
        if (!clientId) {
          setLoading(false);
          return;
        }
        params.clientId = clientId;
      } else if (activeTab === "client-wise" && clientId) {
        params.clientId = clientId; // optional narrowing, still shows breakdown
      }

      const res = await axios.get(`${BASE_URL}/${activeTab}`, { params });
      setReportData(res.data);
    } catch (err) {
      notify(err?.response?.data?.message || "Failed to load report");
    } finally {
      setLoading(false);
    }
  }, [activeTab, startDate, endDate, groupBy, limit, clientId]);

  useEffect(() => {
    setExpandedRows(new Set());
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Bank-wise report has no data until a client is picked — fetch as soon as
  // one is selected instead of waiting for a manual "Apply" click.
  useEffect(() => {
    if (activeTab === "bank-wise" && clientId) {
      fetchReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const clearFilters = () => {
    setStartDate("");
    setEndDate("");
    setClientId("");
    setGroupBy("month");
    setLimit(10);
  };

  const toggleExpand = (key) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  /* ---------------------------------------------------------------- */
  /*  Tab-specific renderers                                            */
  /* ---------------------------------------------------------------- */

  const renderClientOrBankWise = () => {
    if (!reportData) return null;
    const rows = reportData.data || [];
    const totalRevenue = rows.reduce((s, r) => s + (r.totalRevenue || 0), 0);
    const totalInvoices = rows.reduce((s, r) => s + (r.totalInvoices || 0), 0);

    return (
      <>
        <div className="grid grid-cols-3 gap-3 mb-5">
          <StatCard
            label={activeTab === "bank-wise" ? "Bank" : "Total Clients"}
            value={activeTab === "bank-wise" ? rows.length || "—" : rows.length}
          />
          <StatCard
            label="Total Revenue"
            value={formatCurrency(totalRevenue)}
            tone="emerald"
          />
          <StatCard
            label="Total Invoices"
            value={formatNumber(totalInvoices)}
            tone="blue"
          />
        </div>
        {rows.length === 0 ? (
          <EmptyState message="No business data found for this period." />
        ) : (
          <Table
            headers={[
              "",
              "Client",
              "Head Office",
              "GSTIN",
              "Total Revenue",
              // "Invoices",
            ]}
          >
            {rows.map((r) => {
              const isOpen = expandedRows.has(r.clientId);
              return (
                <React.Fragment key={r.clientId}>
                  <tr
                    className="border-b border-blue-50 hover:bg-blue-50/40 transition cursor-pointer"
                    onClick={() => toggleExpand(r.clientId)}
                  >
                    <Td>
                      <RiArrowDownSLine
                        className={`w-4 h-4 text-blue-300 transition-transform ${
                          isOpen ? "rotate-180" : ""
                        }`}
                      />
                    </Td>
                    <Td className="font-semibold text-slate-800">
                      {r.bankName || "—"}
                    </Td>
                    <Td className="text-slate-500">{r.headOffice || "—"}</Td>
                    <Td className="text-slate-500">{r.gstno || "—"}</Td>
                    <Td className="text-emerald-600 font-medium">
                      {formatCurrency(r.totalRevenue)}
                    </Td>
                    {/* <Td>{formatNumber(r.totalInvoices)}</Td> */}
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={5} className="bg-blue-50/30 px-6 py-3">
                        <ClientPeriodBreakdown periods={r.periods} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </Table>
        )}
      </>
    );
  };

  const renderNewspaperWise = () => {
    if (!reportData) return null;
    const rows = reportData.data || [];
    const totalRevenue = rows.reduce((s, r) => s + (r.totalRevenue || 0), 0);
    const totalAds = rows.reduce((s, r) => s + (r.totalAds || 0), 0);

    return (
      <>
        <div className="grid grid-cols-3 gap-3 mb-5">
          <StatCard label="Publications" value={rows.length} />
          <StatCard
            label="Total Revenue"
            value={formatCurrency(totalRevenue)}
            tone="emerald"
          />
          <StatCard
            label="Total Ads"
            value={formatNumber(totalAds)}
            tone="blue"
          />
        </div>
        {rows.length === 0 ? (
          <EmptyState message="No newspaper business data found for this period." />
        ) : (
          <Table
            headers={[
              "",
              "Publication",
              "Total Revenue",
              "Total Ads",
              "Total Volume",
            ]}
          >
            {rows.map((r) => {
              const isOpen = expandedRows.has(r.publication);
              return (
                <React.Fragment key={r.publication}>
                  <tr
                    className="border-b border-blue-50 hover:bg-blue-50/40 transition cursor-pointer"
                    onClick={() => toggleExpand(r.publication)}
                  >
                    <Td>
                      <RiArrowDownSLine
                        className={`w-4 h-4 text-blue-300 transition-transform ${
                          isOpen ? "rotate-180" : ""
                        }`}
                      />
                    </Td>
                    <Td className="font-semibold text-slate-800">
                      {r.publication}
                    </Td>
                    <Td className="text-emerald-600 font-medium">
                      {formatCurrency(r.totalRevenue)}
                    </Td>
                    <Td>{formatNumber(r.totalAds)}</Td>
                    <Td>{formatNumber(r.totalVolume)}</Td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={5} className="bg-blue-50/30 px-6 py-3">
                        <NewspaperPeriodBreakdown periods={r.periods} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </Table>
        )}
      </>
    );
  };

  const renderTopClients = () => {
    if (!reportData) return null;
    const rows = reportData.data || [];
    const totalRevenue = rows.reduce((s, r) => s + (r.totalRevenue || 0), 0);

    return (
      <>
        <div className="grid grid-cols-2 gap-3 mb-5">
          <StatCard
            label={`Top ${rows.length || limit} Revenue`}
            value={formatCurrency(totalRevenue)}
            tone="emerald"
          />
          <StatCard label="Clients Shown" value={rows.length} tone="blue" />
        </div>
        {rows.length === 0 ? (
          <EmptyState message="No client revenue data found for this period." />
        ) : (
          <Table
            headers={[
              "Rank",
              "Client",
              "Head Office",
              "GSTIN",
              "Total Revenue",
              // "Invoices",
            ]}
          >
            {rows.map((r, i) => (
              <tr
                key={r.clientId}
                className="border-b border-blue-50 hover:bg-blue-50/40 transition"
              >
                <Td className="font-bold text-blue-500">#{i + 1}</Td>
                <Td className="font-semibold text-slate-800">
                  {r.bankName || "—"}
                </Td>
                <Td className="text-slate-500">{r.headOffice || "—"}</Td>
                <Td className="text-slate-500">{r.gstno || "—"}</Td>
                <Td className="text-emerald-600 font-medium">
                  {formatCurrency(r.totalRevenue)}
                </Td>
                {/* <Td>{formatNumber(r.invoiceCount)}</Td> */}
              </tr>
            ))}
          </Table>
        )}
      </>
    );
  };

  const renderActiveTab = () => {
    if (loading) return <LoadingRows label="Crunching the numbers…" />;
    switch (activeTab) {
      case "client-wise":
      case "bank-wise":
        return renderClientOrBankWise();
      case "newspaper-wise":
        return renderNewspaperWise();
      case "top-clients":
        return renderTopClients();
      default:
        return null;
    }
  };

  const showDateFilter = true;
  const showGroupBy = activeTab !== "top-clients";
  const showLimit = activeTab === "top-clients";
  const showClientFilter =
    activeTab === "bank-wise" || activeTab === "client-wise";

  return (
    <div className="min-h-screen bg-white text-slate-800">
      <header className="bg-white border-b border-blue-100 sticky top-0 z-30 shadow-sm">
        <div className="w-full mx-auto px-6 py-3.5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white shadow-md shadow-blue-200">
              <RiBriefcase4Line className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-base font-bold text-blue-800 tracking-tight">
                Business Development Reports
              </h1>
              <p className="text-[11px] text-blue-400 tracking-widest uppercase">
                Client · Bank · Newspaper · Top Clients
              </p>
            </div>
          </div>
          <button
            onClick={fetchReport}
            disabled={loading || (activeTab === "bank-wise" && !clientId)}
            title="Refresh"
            className="w-9 h-9 flex items-center justify-center rounded-lg border border-blue-200 text-blue-400 hover:text-blue-600 hover:bg-blue-50 transition disabled:opacity-50"
          >
            <RiRefreshLine
              className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
            />
          </button>
        </div>

        {/* Tab strip */}
        <div className="w-full mx-auto px-6 flex gap-1 overflow-x-auto pb-2 pt-1">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition ${
                  active
                    ? "bg-blue-600 text-white shadow-md shadow-blue-200"
                    : "text-blue-500 hover:bg-blue-50 border border-transparent"
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </header>

      <div className="w-full mx-auto px-6 pt-6 pb-10">
        {/* Filter bar */}
        <div className="bg-white border border-blue-100 rounded-2xl p-4 mb-5 shadow-sm flex items-end gap-4 flex-wrap">
          {showDateFilter && (
            <>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
                  From
                </label>
                <div className="relative">
                  <RiCalendarLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300 pointer-events-none" />
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="bg-blue-50/60 border border-blue-200 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-800 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
                  To
                </label>
                <div className="relative">
                  <RiCalendarLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300 pointer-events-none" />
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="bg-blue-50/60 border border-blue-200 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-800 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
                  />
                </div>
              </div>
            </>
          )}

          {showClientFilter && (
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
                Client{" "}
                {activeTab === "bank-wise" && (
                  <span className="text-red-400">*</span>
                )}
              </label>
              <div className="relative">
                <RiBankLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300 pointer-events-none" />
                {clients.length > 0 ? (
                  <select
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                    className="bg-blue-50/60 border border-blue-200 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-800 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition appearance-none min-w-[200px]"
                  >
                    <option value="">
                      {activeTab === "bank-wise"
                        ? "Select a bank"
                        : "All clients"}
                    </option>
                    {clients.map((c) => (
                      <option key={c._id} value={c._id}>
                        {getClientName(c)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                    placeholder="Client ID"
                    className="bg-blue-50/60 border border-blue-200 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-800 placeholder-blue-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition min-w-[200px]"
                  />
                )}
              </div>
            </div>
          )}

          {showGroupBy && (
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
                Group By
              </label>
              <select
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value)}
                className="bg-blue-50/60 border border-blue-200 rounded-lg px-3 py-2 text-sm text-slate-800 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition appearance-none"
              >
                <option value="month">Month</option>
                <option value="quarter">Quarter</option>
                <option value="year">Year</option>
              </select>
            </div>
          )}

          {showLimit && (
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
                Limit
              </label>
              <input
                type="number"
                min={1}
                max={100}
                value={limit}
                onChange={(e) => setLimit(parseInt(e.target.value, 10) || 10)}
                className="bg-blue-50/60 border border-blue-200 rounded-lg px-3 py-2 text-sm text-slate-800 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition w-24"
              />
            </div>
          )}

          <div className="flex gap-2 ml-auto">
            <button
              onClick={clearFilters}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm text-slate-500 hover:text-slate-700 border border-slate-200 hover:bg-slate-50 transition"
            >
              <RiCloseLine className="w-4 h-4" /> Clear
            </button>
            <button
              onClick={fetchReport}
              disabled={activeTab === "bank-wise" && !clientId}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white transition shadow-md shadow-blue-200 disabled:opacity-50"
            >
              Apply
            </button>
          </div>
        </div>

        {/* Report body */}
        <div className="bg-white border border-blue-100 rounded-2xl p-5 shadow-sm">
          {activeTab === "bank-wise" && !clientId ? (
            <EmptyState message="Select a bank above to view its business report." />
          ) : (
            renderActiveTab()
          )}
        </div>
      </div>

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}
